import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CloseIcon } from './Button';
import './ui.css';

/** An active filter, optionally removable. */
export function Pill({ label, value, onRemove, buttonRef }) {
    return (
        <span className="v2-pill">
            <span className="v2-pill-k">{label}</span>
            <span className="v2-pill-v">{value}</span>
            {onRemove && (
                <button type="button" ref={buttonRef} aria-label={`Remove ${label}: ${value}`} onClick={onRemove}>
                    <CloseIcon />
                </button>
            )}
        </span>
    );
}

Pill.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onRemove: PropTypes.func,
    buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

/**
 * A row of removable pills with focus that never gets lost: after a removal
 * focus moves to the next pill, else the previous one, else `fallbackFocusRef`
 * (e.g. the search field), or the ref `getFallbackFocus(removedId)` returns.
 * "Clear all" also hands focus to the fallback.
 */
export function PillList({
    label, items, onRemove, onClearAll, clearLabel = 'Clear all', fallbackFocusRef, getFallbackFocus,
}) {
    const buttons = useRef(new Map());
    const pending = useRef(null);

    useLayoutEffect(() => {
        const p = pending.current;
        if (!p) return;
        pending.current = null;
        if (p.clear || items.length === 0) {
            const ref = (!p.clear && getFallbackFocus?.(p.id)) || fallbackFocusRef;
            ref?.current?.focus();
            return;
        }
        const target = items[Math.min(p.index, items.length - 1)];
        buttons.current.get(target.id)?.focus();
    }, [items, fallbackFocusRef, getFallbackFocus]);

    if (!items.length) return null;

    return (
        <div className="v2-pills" role="group" aria-label={label}>
            {items.map((item, index) => (
                <Pill
                    key={item.id}
                    label={item.label}
                    value={item.value}
                    buttonRef={(el) => {
                        if (el) buttons.current.set(item.id, el);
                        else buttons.current.delete(item.id);
                    }}
                    onRemove={() => {
                        pending.current = { index, id: item.id };
                        onRemove(item.id);
                    }}
                />
            ))}
            {onClearAll && (
                <button
                    type="button"
                    className="v2-clear"
                    onClick={() => {
                        pending.current = { clear: true };
                        onClearAll();
                    }}
                >
                    {clearLabel}
                </button>
            )}
        </div>
    );
}

PillList.propTypes = {
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })).isRequired,
    onRemove: PropTypes.func.isRequired,
    onClearAll: PropTypes.func,
    clearLabel: PropTypes.string,
    fallbackFocusRef: PropTypes.object,
    // (removedId) => ref: where focus goes when the last pill is removed.
    getFallbackFocus: PropTypes.func,
};
